/**
 * The teacher in PYP mode (B-1): Units of Inquiry (B.26), a learning engagement (B.27), the
 * portfolio (B.28) and the narrative report builder (B.29). Every screen is scoped to one homeroom
 * through `?cohort=`, which is also what keeps the shell in PYP mode while it is open.
 *
 * D-3: no marks field exists anywhere here. Evidence is tagged; portfolios carry narratives; a
 * report is three paragraphs and a school-defined descriptor per strand.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAction, useQuery } from '../../lib/useQuery';
import { Btn, Chip, DesignShell, Empty, ErrorNote, Modal, S, Skeleton, Table, Td, type Crumb } from '../../design/kit';
import { uploadMedia } from '../practice/ui';
import {
  BreadthCounts, ELEMENTS, ElementChips, ExhibitionCard, LpChips, MediaPreview, PieceCard, PypBanner, ToggleChips, kindLabel,
  type Element, type Evidence, type PortfolioView,
} from './ui';

interface ClassHead { id: string; name: string; grade: string; studentCount: number }

/* ------------------------------------------------------------------ class in context */

/** The homeroom from `?cohort=`, defaulting to the teacher's first PYP class. */
function usePypClass() {
  const [params, setParams] = useSearchParams();
  const q = useQuery<{ classes: ClassHead[] }>('/pyp/classes');
  const cohortId = params.get('cohort') ?? q.data?.classes[0]?.id ?? null;
  const pick = (id: string) => setParams(() => new URLSearchParams({ cohort: id }));
  return { q, cohortId, pick, params, setParams };
}

function ClassPicker({ classes, cohortId, pick }: { classes: ClassHead[]; cohortId: string | null; pick: (id: string) => void }) {
  if (classes.length < 2) return null;
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', ...S.small }}>
      Class
      <select className="input" value={cohortId ?? ''} onChange={(e) => pick(e.target.value)} style={{ minHeight: '32px' }}>
        {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
    </label>
  );
}

function PypShell({ title, crumbs, meta, actions, children }: { title: string; crumbs: Crumb[]; meta?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return <DesignShell title={title} crumbs={crumbs} meta={meta} actions={actions} curriculumKey="ib_pyp">{children}</DesignShell>;
}

function NoClasses({ title }: { title: string }) {
  return (
    <PypShell title={title} crumbs={[{ label: title }]}>
      <div style={S.card}><Empty title="No PYP classes" body="These screens open for PYP homerooms. Once you teach one, it appears here." /></div>
    </PypShell>
  );
}

const withCohort = (path: string, cohortId: string | null, extra: Record<string, string> = {}) => {
  const p = new URLSearchParams({ ...(cohortId ? { cohort: cohortId } : {}), ...extra });
  return `${path}?${p.toString()}`;
};

/* ================================================================ B.26 Units of Inquiry */

interface UnitView {
  id: string;
  title: string;
  theme: string;
  centralIdea: string;
  lines: string[];
  keyConcepts: string[];
  learnerProfile: string[];
  elementsTargeted: Element[];
  startDate: string;
  endDate: string;
  engagements: { id: string; title: string; line: number; elements: Element[]; evidenceIn: number; of: number }[];
}

export function PypUnits() {
  const { q: classes, cohortId, pick, params, setParams } = usePypClass();
  const units = useQuery<{ class: ClassHead; units: UnitView[] }>(cohortId ? `/pyp/classes/${cohortId}/units` : null);
  const [adding, setAdding] = useState(false);

  if (classes.data && !classes.data.classes.length) return <NoClasses title="Units of Inquiry" />;
  const d = units.data;
  const today = new Date().toISOString().slice(0, 10);
  const current = d ? [...d.units].reverse().find((u) => u.startDate <= today) ?? d.units[0] : undefined;
  const unit = d?.units.find((u) => u.id === params.get('unit')) ?? current;

  return (
    <PypShell
      title={unit ? unit.title : 'Units of Inquiry'}
      crumbs={[{ label: 'Units of Inquiry', to: withCohort('/teacher/pyp/units', cohortId) }, ...(unit ? [{ label: unit.theme || unit.title }] : [])]}
      meta={d ? <span>{d.class.name} · {d.class.grade}{unit ? ` · ${new Date(unit.startDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} – ${new Date(unit.endDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}` : ''}</span> : undefined}
      actions={
        <>
          <ClassPicker classes={classes.data?.classes ?? []} cohortId={cohortId} pick={pick} />
          {unit ? <Btn kind="secondarySm" to={`/teacher/unit-plans/${unit.id}`}>Edit unit</Btn> : null}
          {unit ? <Btn kind="primarySm" onClick={() => setAdding(true)}>Add engagement</Btn> : null}
        </>
      }
    >
      <PypBanner grade={d?.class.grade} />
      {classes.error || units.error ? <ErrorNote error={classes.error ?? units.error} retry={() => { classes.reload(); units.reload(); }} /> : null}
      {!d ? (classes.error || units.error ? null : <Skeleton variant="detail" />) : d.units.length === 0 ? (
        <div style={S.card}><Empty title="No units of inquiry yet" body="Plan a unit for this homeroom and it appears here with its engagements." action={<Btn kind="primarySm" to="/teacher/unit-plans/new">Plan a unit</Btn>} /></div>
      ) : unit ? (
        <>
          {d.units.length > 1 ? (
            <nav aria-label="Units" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {d.units.map((u) => (
                <button key={u.id} type="button" aria-current={u.id === unit.id ? 'page' : undefined} onClick={() => setParams({ cohort: cohortId!, unit: u.id })}
                  className={`kit-btn ${u.id === unit.id ? 'kit-btn--primary' : 'kit-btn--secondary'}`} style={{ fontSize: '0.75rem', padding: '0 12px', minHeight: '32px' }}>
                  {u.theme || u.title}
                </button>
              ))}
            </nav>
          ) : null}
          <section style={{ ...S.card, display: 'grid', gap: '16px' }} aria-label="Unit of inquiry">
            <div>
              <h2 style={{ ...S.h2, margin: 0 }}>{unit.title}</h2>
              <div style={S.sub}>Transdisciplinary theme · {unit.theme || '—'}{unit.keyConcepts.length ? ` · Key concepts: ${unit.keyConcepts.join(', ')}` : ''}</div>
            </div>
            <div className="dashgrid">
              <div style={{ display: 'grid', gap: '14px' }}>
                <div>
                  <div style={S.sectionLabel}>Central idea</div>
                  <p style={{ ...S.prose, margin: '6px 0 0' }}>{unit.centralIdea || '—'}</p>
                </div>
                <div>
                  <div style={S.sectionLabel}>Lines of inquiry</div>
                  <ol style={{ margin: '6px 0 0', paddingLeft: '20px', display: 'grid', gap: '4px' }}>
                    {unit.lines.map((l) => <li key={l} style={S.small}>{l}</li>)}
                  </ol>
                </div>
              </div>
              <div style={{ display: 'grid', gap: '14px', alignContent: 'start' }}>
                <div>
                  <div style={{ ...S.sectionLabel, marginBottom: '6px' }}>Essential elements targeted</div>
                  <ElementChips elements={unit.elementsTargeted} />
                </div>
                <div>
                  <div style={{ ...S.sectionLabel, marginBottom: '6px' }}>Learner Profile focus</div>
                  <LpChips attributes={unit.learnerProfile} />
                </div>
              </div>
            </div>
          </section>
          <Table minWidth={560} columns={[{ label: 'Learning engagement' }, { label: 'Line', width: '60px' }, { label: 'Elements' }, { label: 'Evidence in', width: '110px', align: 'right' }]}>
            {unit.engagements.length === 0 ? (
              <tr><Td style={{ padding: 0 }}><Empty title="No engagements yet" body="Add the first learning engagement for this unit." /></Td><Td /><Td /><Td /></tr>
            ) : unit.engagements.map((e) => (
              <tr key={e.id}>
                <Td><Link to={withCohort(`/teacher/pyp/engagements/${e.id}`, cohortId)} style={{ ...S.link, color: 'var(--c-ink)' }}>{e.title}</Link></Td>
                <Td>{e.line}</Td>
                <Td><ElementChips elements={e.elements} /></Td>
                <Td align="right"><span style={{ fontVariantNumeric: 'tabular-nums' }}>{e.evidenceIn} of {e.of}</span></Td>
              </tr>
            ))}
          </Table>
          {adding ? <AddEngagement cohortId={cohortId!} unit={unit} onClose={() => setAdding(false)} onAdded={() => { setAdding(false); units.reload(); }} /> : null}
        </>
      ) : null}
    </PypShell>
  );
}

function AddEngagement({ cohortId, unit, onClose, onAdded }: { cohortId: string; unit: UnitView; onClose: () => void; onAdded: () => void }) {
  const [title, setTitle] = useState('');
  const [line, setLine] = useState(1);
  const [elements, setElements] = useState<string[]>([]);
  const save = useAction(async () => {
    await api.post(`/pyp/classes/${cohortId}/units/${unit.id}/engagements`, { title, line, elements });
    onAdded();
  });
  return (
    <Modal
      title="Add a learning engagement"
      blurb={`Tie it to a line of inquiry in ${unit.title} and name the elements it targets.`}
      onClose={onClose}
      footer={<><Btn kind="secondarySm" onClick={onClose}>Cancel</Btn><Btn kind="primarySm" disabled={!title.trim() || !elements.length || save.pending} onClick={() => save.run()}>{save.pending ? 'Adding…' : 'Add engagement'}</Btn></>}
    >
      <div style={{ display: 'grid', gap: '14px' }}>
        <label style={{ display: 'grid', gap: '5px', ...S.small, fontWeight: 600 }}>
          Title
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} />
        </label>
        <label style={{ display: 'grid', gap: '5px', ...S.small, fontWeight: 600 }}>
          Line of inquiry
          <select className="input" value={line} onChange={(e) => setLine(Number(e.target.value))}>
            {(unit.lines.length ? unit.lines : ['Line 1']).map((l, i) => <option key={l} value={i + 1}>{i + 1}. {l}</option>)}
          </select>
        </label>
        <ToggleChips label="Elements targeted" options={ELEMENTS} value={elements} onChange={setElements} />
        {save.error ? <ErrorNote error={save.error} /> : null}
      </div>
    </Modal>
  );
}

/* ================================================================ B.27 learning engagement */

interface EngagementPayload {
  class: ClassHead;
  unit: { id: string; title: string; theme: string; lines: string[] };
  engagement: { id: string; title: string; line: number; elements: Element[] };
  withEvidence: number;
  rows: { studentId: string; name: string; evidence: Evidence[] }[];
  options: { elements: Element[]; learnerProfile: string[] };
  canEdit: boolean;
}

export function PypEngagement() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const cohortId = params.get('cohort');
  const q = useQuery<EngagementPayload>(cohortId ? `/pyp/classes/${cohortId}/engagements/${id}` : null, [id]);
  const [picked, setPicked] = useState<{ studentId: string; evidenceId: string | null } | null>(null);
  const d = q.data;

  const crumbs: Crumb[] = [
    { label: 'Units of Inquiry', to: withCohort('/teacher/pyp/units', cohortId) },
    ...(d ? [{ label: d.unit.theme || d.unit.title, to: withCohort('/teacher/pyp/units', cohortId, { unit: d.unit.id }) }, { label: d.engagement.title }] : []),
  ];
  if (!cohortId) return <NoClasses title="Learning engagement" />;
  const row = d && picked ? d.rows.find((r) => r.studentId === picked.studentId) : undefined;
  const ev = row?.evidence.find((e) => e.id === picked?.evidenceId) ?? null;

  return (
    <PypShell title={d?.engagement.title ?? 'Learning engagement'} crumbs={crumbs} meta={d ? <span>{d.withEvidence} of {d.rows.length} have evidence</span> : undefined}>
      <PypBanner grade={d?.class.grade} />
      {q.error ? <ErrorNote error={q.error} retry={q.reload} /> : null}
      {!d ? (q.error ? null : <Skeleton variant="detail" />) : (
        <>
          <p style={{ ...S.small, ...S.muted, margin: 0 }}>Line of inquiry {d.engagement.line}{d.unit.lines[d.engagement.line - 1] ? ` · ${d.unit.lines[d.engagement.line - 1]}` : ''} · photos, audio and written reflections accepted</p>
          <div className="dashgrid">
            <Table minWidth={560} columns={[{ label: 'Student' }, { label: 'Evidence' }, { label: 'Elements seen' }, { label: 'Learner Profile' }]}>
              {d.rows.flatMap((r) => (r.evidence.length ? r.evidence : [null]).map((e, i) => (
                <tr
                  key={`${r.studentId}-${e?.id ?? 'none'}`}
                  onClick={() => setPicked({ studentId: r.studentId, evidenceId: e?.id ?? null })}
                  style={{ cursor: 'pointer', background: picked?.studentId === r.studentId && picked.evidenceId === (e?.id ?? null) ? 'var(--c-surface-3)' : undefined }}
                >
                  <Td>{i === 0 ? <span style={{ color: 'var(--c-ink)', fontWeight: 500 }}>{r.name}</span> : null}</Td>
                  <Td>{e ? <span>{e.title}{e.sample ? <span style={{ ...S.mutedXs, display: 'block' }}>{kindLabel(e)} · sample</span> : <span style={{ ...S.mutedXs, display: 'block' }}>{kindLabel(e)}</span>}</span> : <span style={S.mutedXs}>Not yet uploaded</span>}</Td>
                  <Td>{e ? <ElementChips elements={e.elements} /> : '—'}</Td>
                  <Td>{e ? <LpChips attributes={e.learnerProfile} /> : '—'}</Td>
                </tr>
              )))}
            </Table>
            <aside style={{ display: 'grid', gap: '12px', alignContent: 'start' }}>
              {picked && row ? (
                ev ? (
                  <TagPanel key={ev.id} evidence={ev} name={row.name} options={d.options} canEdit={d.canEdit} onSaved={q.reload} />
                ) : d.canEdit ? (
                  <AddEvidence key={row.studentId} cohortId={cohortId} engagementId={d.engagement.id} studentId={row.studentId} name={row.name} options={d.options} onSaved={() => q.reload()} />
                ) : (
                  <div style={S.card}><Empty title="Nothing uploaded yet" /></div>
                )
              ) : (
                <div style={{ ...S.card, ...S.small, ...S.muted }}>Pick a row to tag its evidence, or add evidence for a student who has none yet.</div>
              )}
              <p style={{ ...S.mutedXs, margin: 0 }}>No marks field exists in PYP mode. Evidence flows to each student’s portfolio and to the narrative report.</p>
            </aside>
          </div>
        </>
      )}
    </PypShell>
  );
}

function TagPanel({ evidence, name, options, canEdit, onSaved }: { evidence: Evidence; name: string; options: EngagementPayload['options']; canEdit: boolean; onSaved: () => void }) {
  const [elements, setElements] = useState<string[]>(evidence.elements);
  const [lp, setLp] = useState<string[]>(evidence.learnerProfile);
  const [note, setNote] = useState(evidence.note);
  const [saved, setSaved] = useState(false);
  const save = useAction(async () => {
    await api.patch(`/pyp/evidence/${evidence.id}`, { elements, learnerProfile: lp, note });
    setSaved(true);
    onSaved();
  });
  return (
    <section aria-label="Tag this evidence" style={{ ...S.card, display: 'grid', gap: '12px' }}>
      <div>
        <h2 style={{ ...S.h3, margin: 0 }}>Tag this evidence</h2>
        <div style={S.sub}>{name} · {kindLabel(evidence)} · {evidence.title}</div>
      </div>
      <MediaPreview evidence={evidence} />
      <ToggleChips label="Essential elements" options={options.elements} value={elements} onChange={(v) => { setElements(v); setSaved(false); }} />
      <ToggleChips label="Learner Profile" options={options.learnerProfile} value={lp} onChange={(v) => { setLp(v); setSaved(false); }} />
      <label style={{ display: 'grid', gap: '5px', ...S.small, fontWeight: 600 }}>
        Your note
        <textarea className="input" rows={3} value={note} onChange={(e) => { setNote(e.target.value); setSaved(false); }} maxLength={1000} disabled={!canEdit} />
      </label>
      {save.error ? <ErrorNote error={save.error} /> : null}
      {canEdit ? (
        <div style={S.between}>
          <span role="status" style={{ ...S.mutedXs, color: saved ? 'var(--c-good)' : undefined }}>{saved ? 'Saved.' : ''}</span>
          <Btn kind="primarySm" disabled={save.pending} onClick={() => save.run()}>{save.pending ? 'Saving…' : 'Save tags'}</Btn>
        </div>
      ) : null}
    </section>
  );
}

function AddEvidence({ cohortId, engagementId, studentId, name, options, onSaved }: { cohortId: string; engagementId: string; studentId: string; name: string; options: EngagementPayload['options']; onSaved: () => void }) {
  const [kind, setKind] = useState<'photo' | 'audio' | 'text'>('photo');
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [elements, setElements] = useState<string[]>([]);
  const [lp, setLp] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const save = useAction(async () => {
    const mediaId = kind === 'text' ? undefined : await uploadMedia(kind, file!);
    await api.post(`/pyp/classes/${cohortId}/evidence`, {
      studentId, engagementId, kind, title: title.trim() || (kind === 'text' ? 'Written reflection' : kind === 'photo' ? 'Photo' : 'Recording'),
      ...(mediaId ? { mediaId } : { text }), elements, learnerProfile: lp, note,
    });
    onSaved();
  });
  const ready = kind === 'text' ? text.trim().length > 0 : Boolean(file);
  return (
    <section aria-label="Add evidence" style={{ ...S.card, display: 'grid', gap: '12px' }}>
      <div>
        <h2 style={{ ...S.h3, margin: 0 }}>Add evidence</h2>
        <div style={S.sub}>{name}</div>
      </div>
      <div role="radiogroup" aria-label="Kind" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {(['photo', 'audio', 'text'] as const).map((k) => (
          <button key={k} type="button" role="radio" aria-checked={kind === k} onClick={() => { setKind(k); setFile(null); }}
            className={`kit-btn ${kind === k ? 'kit-btn--primary' : 'kit-btn--secondary'}`} style={{ fontSize: '0.75rem', padding: '0 12px', minHeight: '32px' }}>
            {k === 'photo' ? 'Photo' : k === 'audio' ? 'Audio' : 'Written'}
          </button>
        ))}
      </div>
      {kind === 'text' ? (
        <label style={{ display: 'grid', gap: '5px', ...S.small, fontWeight: 600 }}>
          Reflection or observation
          <textarea className="input" rows={4} value={text} onChange={(e) => setText(e.target.value)} maxLength={4000} />
        </label>
      ) : (
        <label style={{ display: 'grid', gap: '5px', ...S.small, fontWeight: 600 }}>
          {kind === 'photo' ? 'Photo' : 'Recording'}
          <input type="file" accept={kind === 'photo' ? 'image/jpeg,image/png,image/webp' : 'audio/*'} capture={kind === 'photo' ? 'environment' : undefined} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </label>
      )}
      <label style={{ display: 'grid', gap: '5px', ...S.small, fontWeight: 600 }}>
        What it is
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} placeholder="Photo log of 12 taps" />
      </label>
      <ToggleChips label="Essential elements" options={options.elements} value={elements} onChange={setElements} />
      <ToggleChips label="Learner Profile" options={options.learnerProfile} value={lp} onChange={setLp} />
      <label style={{ display: 'grid', gap: '5px', ...S.small, fontWeight: 600 }}>
        Your note
        <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
      </label>
      {save.error ? <ErrorNote error={save.error} /> : null}
      <Btn kind="primarySm" disabled={!ready || save.pending} onClick={() => save.run()}>{save.pending ? 'Saving…' : 'Add evidence'}</Btn>
    </section>
  );
}

/* ================================================================ B.28 portfolio */

export function PypPortfolio() {
  const { q: classes, cohortId, pick, params, setParams } = usePypClass();
  const studentId = params.get('student');
  const list = useQuery<{ class: ClassHead; students: { id: string; name: string; evidence: number; pieces: number; shared: number }[] }>(cohortId && !studentId ? `/pyp/classes/${cohortId}/portfolio` : null);
  const one = useQuery<PortfolioView & { class: ClassHead; canEdit: boolean }>(cohortId && studentId ? `/pyp/classes/${cohortId}/portfolio/${studentId}` : null);
  const [adding, setAdding] = useState(false);
  const share = useAction(async () => { await api.post(`/pyp/classes/${cohortId}/portfolio/${studentId}/share`, { shared: true }); one.reload(); });
  const remove = useAction(async (id: string) => { await api.del(`/pyp/pieces/${id}`); one.reload(); });

  if (classes.data && !classes.data.classes.length) return <NoClasses title="Portfolio" />;
  const root: Crumb = { label: 'Portfolio', to: withCohort('/teacher/pyp/portfolio', cohortId) };

  if (studentId) {
    const d = one.data;
    return (
      <PypShell
        title={d ? `${d.student.name}’s portfolio` : 'Portfolio'}
        crumbs={[root, { label: d?.student.name ?? '…' }]}
        meta={<span>Curated by the student and their teacher. Each piece carries a short narrative, not a score.</span>}
        actions={d?.canEdit ? (
          <>
            <Btn kind="secondarySm" disabled={d.allShared || !d.pieces.length || share.pending} onClick={() => share.run()}>{d.allShared ? 'Shared with family' : share.pending ? 'Sharing…' : 'Share with family'}</Btn>
            <Btn kind="primarySm" disabled={!d.available.length} onClick={() => setAdding(true)}>Add piece</Btn>
          </>
        ) : undefined}
      >
        <PypBanner grade={d?.class.grade} />
        {one.error || share.error || remove.error ? <ErrorNote error={one.error ?? share.error ?? remove.error} retry={one.reload} /> : null}
        {!d ? (one.error ? null : <Skeleton variant="detail" />) : (
          <div className="dashgrid">
            <div style={{ display: 'grid', gap: '12px', alignContent: 'start' }}>
              {d.pieces.length === 0 ? (
                <div style={S.card}><Empty title="No pieces yet" body="Choose evidence with the student and add a sentence on why it matters." /></div>
              ) : d.pieces.map((p) => <PieceCard key={p.id} piece={p} onRemove={d.canEdit ? () => remove.run(p.id) : undefined} />)}
            </div>
            <div style={{ display: 'grid', gap: '12px', alignContent: 'start' }}>
              <BreadthCounts counts={d.breadth} thin={d.thin} name={d.student.name} />
              {d.exhibition ? <ExhibitionCard exhibition={d.exhibition} /> : null}
            </div>
          </div>
        )}
        {adding && d ? <AddPiece cohortId={cohortId!} studentId={studentId} available={d.available} onClose={() => setAdding(false)} onAdded={() => { setAdding(false); one.reload(); }} /> : null}
      </PypShell>
    );
  }

  const d = list.data;
  return (
    <PypShell title="Portfolio" crumbs={[{ label: 'Portfolio' }]} meta={d ? <span>{d.class.name} · curated evidence with narrative</span> : undefined} actions={<ClassPicker classes={classes.data?.classes ?? []} cohortId={cohortId} pick={pick} />}>
      <PypBanner grade={d?.class.grade} />
      {list.error || classes.error ? <ErrorNote error={list.error ?? classes.error} retry={list.reload} /> : null}
      {!d ? (list.error ? null : <Skeleton variant="detail" />) : (
        <Table minWidth={480} columns={[{ label: 'Student' }, { label: 'Evidence', width: '100px', align: 'right' }, { label: 'Pieces', width: '90px', align: 'right' }, { label: 'Family', width: '150px' }]}>
          {d.students.map((s) => (
            <tr key={s.id}>
              <Td><button type="button" onClick={() => setParams({ cohort: cohortId!, student: s.id })} style={{ ...S.link, color: 'var(--c-ink)', background: 'none', border: 'none', padding: 0, fontSize: '0.7812rem' }}>{s.name}</button></Td>
              <Td align="right">{s.evidence}</Td>
              <Td align="right">{s.pieces}</Td>
              <Td>{s.pieces === 0 ? <span style={S.mutedXs}>—</span> : s.shared === s.pieces ? <Chip tone="good">Shared</Chip> : <Chip>Not shared</Chip>}</Td>
            </tr>
          ))}
        </Table>
      )}
    </PypShell>
  );
}

function AddPiece({ cohortId, studentId, available, onClose, onAdded }: { cohortId: string; studentId: string; available: Evidence[]; onClose: () => void; onAdded: () => void }) {
  const [evidenceId, setEvidenceId] = useState(available[0]?.id ?? '');
  const [title, setTitle] = useState('');
  const [narrative, setNarrative] = useState('');
  const save = useAction(async () => {
    await api.post(`/pyp/classes/${cohortId}/portfolio/${studentId}/pieces`, { evidenceId, narrative, ...(title.trim() ? { title } : {}) });
    onAdded();
  });
  return (
    <Modal
      title="Add a portfolio piece"
      blurb="Pick the evidence and say, in a sentence, why it matters. No score."
      onClose={onClose}
      footer={<><Btn kind="secondarySm" onClick={onClose}>Cancel</Btn><Btn kind="primarySm" disabled={!evidenceId || !narrative.trim() || save.pending} onClick={() => save.run()}>{save.pending ? 'Adding…' : 'Add piece'}</Btn></>}
    >
      <div style={{ display: 'grid', gap: '14px' }}>
        <label style={{ display: 'grid', gap: '5px', ...S.small, fontWeight: 600 }}>
          Evidence
          <select className="input" value={evidenceId} onChange={(e) => setEvidenceId(e.target.value)}>
            {available.map((e) => <option key={e.id} value={e.id}>{e.title} · {kindLabel(e)} · {e.elements.join(', ') || 'untagged'}</option>)}
          </select>
        </label>
        <label style={{ display: 'grid', gap: '5px', ...S.small, fontWeight: 600 }}>
          Title (optional)
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} />
        </label>
        <label style={{ display: 'grid', gap: '5px', ...S.small, fontWeight: 600 }}>
          Narrative
          <textarea className="input" rows={3} value={narrative} onChange={(e) => setNarrative(e.target.value)} maxLength={600} placeholder="Second attempt shows evaporation from the lake, which the first missed." />
        </label>
        {save.error ? <ErrorNote error={save.error} /> : null}
      </div>
    </Modal>
  );
}

/* ================================================================ B.29 narrative reports */

interface Scale { levels: { name: string; descriptor: string }[]; strands: string[]; allowAssessments: boolean }
type Status = 'none' | 'draft' | 'approved' | 'released';
interface Sections { academic: string; personal: string; nextSteps: string }
interface ReportPayload {
  class: ClassHead;
  term: string;
  student: { id: string; name: string };
  scale: Scale;
  report: { id: string; sections: Sections; levels: Record<string, string>; status: Status; draftSource: 'ai' | 'fallback' | null; edited: boolean } | null;
  sources: { evidence: (Evidence & { unit: string })[]; pieces: { id: string; title: string; narrative: string; evidenceId: string }[] };
  prevId: string | null;
  nextId: string | null;
  canEdit: boolean;
}

const STATUS_CHIP: Record<Status, ReactNode> = {
  none: <Chip>Not started</Chip>,
  draft: <Chip tone="warn">Draft</Chip>,
  approved: <Chip tone="good">Approved</Chip>,
  released: <Chip tone="brand">Released</Chip>,
};

export function PypReports() {
  const { q: classes, cohortId, pick, params, setParams } = usePypClass();
  const studentId = params.get('student');
  const list = useQuery<{ class: ClassHead; term: string; drafted: number; approved: number; total: number; students: { id: string; name: string; evidence: number; status: Status }[]; canRelease: boolean }>(
    cohortId ? `/pyp/classes/${cohortId}/reports` : null, [studentId],
  );
  const release = useAction(async () => { await api.post(`/pyp/classes/${cohortId}/reports/release`); list.reload(); });

  if (classes.data && !classes.data.classes.length) return <NoClasses title="Narrative reports" />;
  if (studentId && cohortId) return <ReportBuilder key={studentId} cohortId={cohortId} studentId={studentId} drafted={list.data ? `${list.data.drafted} of ${list.data.total} drafted` : ''} go={(id) => setParams(id ? { cohort: cohortId, student: id } : { cohort: cohortId })} />;

  const d = list.data;
  return (
    <PypShell
      title="Narrative reports"
      crumbs={[{ label: 'Narrative reports' }]}
      meta={d ? <span>{d.class.name} · {d.drafted} of {d.total} drafted · {d.approved} approved</span> : undefined}
      actions={
        <>
          <ClassPicker classes={classes.data?.classes ?? []} cohortId={cohortId} pick={pick} />
          {d?.canRelease ? <Btn kind="primarySm" disabled={!d.approved || release.pending} onClick={() => release.run()}>Release approved to families</Btn> : null}
        </>
      }
    >
      <PypBanner grade={d?.class.grade} />
      <p style={{ ...S.small, ...S.muted, margin: 0 }}>Drafts are written from tagged evidence only. You edit and approve; the coordinator releases.</p>
      {list.error || release.error ? <ErrorNote error={list.error ?? release.error} retry={list.reload} /> : null}
      {!d ? (list.error ? null : <Skeleton variant="detail" />) : (
        <Table minWidth={460} columns={[{ label: 'Student' }, { label: 'Tagged evidence', width: '140px', align: 'right' }, { label: 'Status', width: '130px' }]}>
          {d.students.map((s) => (
            <tr key={s.id}>
              <Td><button type="button" onClick={() => setParams({ cohort: cohortId!, student: s.id })} style={{ ...S.link, color: 'var(--c-ink)', background: 'none', border: 'none', padding: 0, fontSize: '0.7812rem' }}>{s.name}</button></Td>
              <Td align="right">{s.evidence}</Td>
              <Td>{STATUS_CHIP[s.status]}</Td>
            </tr>
          ))}
        </Table>
      )}
    </PypShell>
  );
}

const SECTION_LABEL: Record<keyof Sections, string> = { academic: 'Academic growth', personal: 'Personal growth', nextSteps: 'Next steps' };

function ReportBuilder({ cohortId, studentId, drafted, go }: { cohortId: string; studentId: string; drafted: string; go: (id: string | null) => void }) {
  const navigate = useNavigate();
  const q = useQuery<ReportPayload>(`/pyp/classes/${cohortId}/reports/${studentId}`, [studentId]);
  const d = q.data;
  const [sections, setSections] = useState<Sections>({ academic: '', personal: '', nextSteps: '' });
  const [levels, setLevels] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState('');
  useEffect(() => {
    setSections(d?.report?.sections ?? { academic: '', personal: '', nextSteps: '' });
    setLevels(d?.report?.levels ?? {});
    setNotice('');
  }, [d]);

  const draft = useAction(async () => {
    const res = await api.post<{ report: NonNullable<ReportPayload['report']> }>(`/pyp/classes/${cohortId}/reports/${studentId}/draft`);
    q.setData((prev) => ({ ...prev!, report: res.report }));
  });
  const save = useAction(async (approve: boolean) => {
    const res = await api.put<{ report: NonNullable<ReportPayload['report']>; nextId: string | null }>(`/pyp/classes/${cohortId}/reports/${studentId}`, { sections, levels, approve });
    if (approve) {
      if (res.nextId) go(res.nextId);
      else navigate(withCohort('/teacher/pyp/reports', cohortId));
    } else {
      q.setData((prev) => ({ ...prev!, report: res.report }));
      setNotice('Saved.');
    }
  });
  const locked = !d?.canEdit || d.report?.status === 'released';
  const dirty = useMemo(() => Boolean(d && (JSON.stringify(sections) !== JSON.stringify(d.report?.sections ?? { academic: '', personal: '', nextSteps: '' }) || JSON.stringify(levels) !== JSON.stringify(d.report?.levels ?? {}))), [d, sections, levels]);

  return (
    <PypShell
      title={d ? `${d.student.name} · narrative` : 'Narrative'}
      crumbs={[{ label: 'Narrative reports', to: withCohort('/teacher/pyp/reports', cohortId) }, { label: d?.student.name ?? '…' }]}
      meta={<span>{drafted || 'AI drafts from tagged evidence only. You edit, the coordinator releases.'}</span>}
      actions={d ? (
        <>
          <Btn kind="secondarySm" disabled={!d.prevId} onClick={() => go(d.prevId)}>Previous</Btn>
          {!locked ? <Btn kind="secondarySm" disabled={!dirty || save.pending} onClick={() => save.run(false)}>Save</Btn> : null}
          {!locked ? <Btn kind="primarySm" disabled={save.pending || !sections.academic.trim()} onClick={() => save.run(true)}>Approve and next</Btn> : null}
        </>
      ) : undefined}
    >
      <PypBanner grade={d?.class.grade} />
      {q.error || draft.error || save.error ? <ErrorNote error={q.error ?? draft.error ?? save.error} retry={q.reload} /> : null}
      {notice ? <div role="status" style={{ ...S.small, color: 'var(--c-good)' }}>{notice}</div> : null}
      {!d ? (q.error ? null : <Skeleton variant="detail" />) : (
        <div className="dashgrid">
          <section aria-label="Draft narrative" style={{ ...S.card, display: 'grid', gap: '14px' }}>
            <div style={S.between}>
              <h2 style={{ ...S.h3, margin: 0 }}>Draft narrative</h2>
              <span style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                {d.report ? STATUS_CHIP[d.report.status] : null}
                {d.report?.draftSource ? <span style={S.mutedXs}>{d.report.draftSource === 'ai' ? 'AI draft' : 'Draft from your tags'}{d.report.edited ? ' · edited by you' : ''}</span> : null}
              </span>
            </div>
            {!d.report && d.sources.evidence.length === 0 ? (
              <Empty title="No tagged evidence yet" body="The draft is written from tagged evidence only. Tag some evidence in a learning engagement first." />
            ) : !d.report ? (
              <Empty title="Not drafted yet" body={`Draft from ${d.sources.evidence.length} tagged piece${d.sources.evidence.length === 1 ? '' : 's'} of evidence. You can edit every word.`} action={d.canEdit ? <Btn kind="primarySm" disabled={draft.pending} onClick={() => draft.run()}>{draft.pending ? 'Drafting…' : 'Draft from tagged evidence'}</Btn> : undefined} />
            ) : (
              <>
                {(Object.keys(SECTION_LABEL) as (keyof Sections)[]).map((k) => (
                  <label key={k} style={{ display: 'grid', gap: '5px' }}>
                    <span style={S.sectionLabel}>{SECTION_LABEL[k]}</span>
                    <textarea className="input" rows={k === 'nextSteps' ? 3 : 5} value={sections[k]} disabled={locked} onChange={(e) => { setSections({ ...sections, [k]: e.target.value }); setNotice(''); }} style={{ ...S.prose }} />
                  </label>
                ))}
                {!locked ? <div><Btn kind="secondarySm" disabled={draft.pending} onClick={() => draft.run()}>{draft.pending ? 'Redrafting…' : 'Redraft from tagged evidence'}</Btn></div> : null}
              </>
            )}
          </section>
          <aside style={{ display: 'grid', gap: '12px', alignContent: 'start' }}>
            <section aria-label="Descriptor levels" style={{ ...S.card, display: 'grid', gap: '10px' }}>
              <div>
                <h2 style={{ ...S.h3, margin: 0 }}>Descriptor levels</h2>
                <div style={S.sub}>The school’s own scale</div>
              </div>
              {d.scale.strands.map((s) => (
                <label key={s} style={{ display: 'grid', gap: '4px' }}>
                  <span style={{ ...S.between, ...S.small }}>
                    <span>{s}</span>
                    <select className="input" value={levels[s] ?? ''} disabled={locked} onChange={(e) => { setLevels({ ...levels, [s]: e.target.value }); setNotice(''); }} style={{ minHeight: '30px', maxWidth: '150px' }} aria-label={`${s} level`}>
                      <option value="">Choose…</option>
                      {d.scale.levels.map((l) => <option key={l.name} value={l.name}>{l.name}</option>)}
                    </select>
                  </span>
                  {levels[s] ? <span style={S.mutedXs}>{d.scale.levels.find((l) => l.name === levels[s])?.descriptor}</span> : null}
                </label>
              ))}
            </section>
            <section aria-label="Sources" style={{ ...S.card, display: 'grid', gap: '6px' }}>
              <h2 style={{ ...S.h3, margin: 0 }}>Sources</h2>
              {d.sources.evidence.slice(0, 8).map((e) => <span key={e.id} style={S.small}>{kindLabel(e)} · {e.title}</span>)}
              <span style={S.mutedXs}>{d.sources.pieces.length} portfolio piece{d.sources.pieces.length === 1 ? '' : 's'}, {d.sources.evidence.length} tagged piece{d.sources.evidence.length === 1 ? '' : 's'} of evidence</span>
            </section>
          </aside>
        </div>
      )}
    </PypShell>
  );
}
